import React from "react";
import { Button, Card } from "antd";
import { ArrowLeft, Printer, Download, RotateCcw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

interface ReportPageProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  backPath: string;
  stats?: Array<{ title: string; value: string; colorClass?: string }>;
  onExportCSV?: () => void;
  isExportDisabled?: boolean;
  onRefresh?: () => void;
  filterControls?: React.ReactNode;
  isCustomRender?: boolean;
  children?: React.ReactNode;
}

export const ReportPage: React.FC<ReportPageProps> = ({
  title,
  description,
  icon,
  backPath,
  stats,
  onExportCSV,
  isExportDisabled = false,
  onRefresh,
  filterControls,
  isCustomRender = false,
  children,
}) => {
  const navigate = useNavigate();

  return (
    <div className="p-6 min-h-screen bg-gray-50 flex flex-col gap-6 printable-report">
      {/* Detail Header Control Bar */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-gray-200 pb-4 no-print">
        <div className="flex items-center gap-3.5">
          <Button
            type="text"
            icon={<ArrowLeft className="w-5 h-5 text-gray-600" />}
            onClick={() => navigate(-1)}
            className="flex items-center justify-center p-1.5 hover:bg-gray-150 rounded-lg no-print"
          />
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-white rounded-xl shadow-sm border border-gray-150">
              {icon}
            </div>
            <div className="flex flex-col gap-0.5">
              <h1 className="text-base font-bold text-gray-900 leading-tight">
                {title}
              </h1>
              <p className="text-xs text-gray-500">{description}</p>
            </div>
          </div>
        </div>

        {/* Global actions */}
        <div className="flex items-center gap-2 self-end sm:self-auto">
          <Button
            icon={<Printer className="w-4 h-4" />}
            className="text-xs font-semibold hover:border-gray-300 rounded-lg"
            onClick={() => window.print()}
          >
            Print
          </Button>
          {onExportCSV && (
            <Button
              type="primary"
              icon={<Download className="w-4 h-4" />}
              className="bg-indigo-600 border-none text-xs hover:bg-indigo-700 font-semibold rounded-lg"
              onClick={onExportCSV}
              disabled={isExportDisabled}
            >
              Export CSV
            </Button>
          )}
          {onRefresh && (
            <Button
              shape="circle"
              icon={<RotateCcw className="w-4 h-4 text-gray-500" />}
              onClick={onRefresh}
              className="hover:border-gray-300"
            />
          )}
        </div>
      </div>

      {/* Print view header */}
      {!isCustomRender && (
        <div className="hidden print:block text-center border-b pb-4 mb-4">
          <h1 className="text-2xl font-black text-gray-955 uppercase leading-tight">
            {title}
          </h1>
          <p className="text-xs text-gray-600 font-medium mt-1">
            Generated: {dayjs().format("YYYY-MM-DD HH:mm")}
          </p>
        </div>
      )}

      {/* Stats Summary Cards Row */}
      {!isCustomRender && stats && stats.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6 no-print">
          {stats.map((stat, idx) => (
            <Card
              key={idx}
              bordered={false}
              className="bg-white rounded-2xl border border-gray-150/80 shadow-sm p-4 hover:shadow-md transition-shadow"
              bodyStyle={{ padding: 0 }}
            >
              <div className="flex flex-col gap-1.5 font-sans">
                <span className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">
                  {stat.title}
                </span>
                <span
                  className={`text-base font-black ${stat.colorClass || "text-gray-900"}`}
                >
                  {stat.value}
                </span>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Interactive Filter Control Panels */}
      {!isCustomRender && filterControls && (
        <div className="bg-white p-5 rounded-2xl border border-gray-200/80 shadow-sm flex flex-col gap-4 no-print">
          {filterControls}
        </div>
      )}

      {/* Main Content */}
      {children}

      {/* Printing specific styles to append */}
      <style>{`
        @media print {
          body {
            background: #fff !important;
            color: #000 !important;
          }
          aside, nav, header, .sidebar, .topbar, .navbar, .no-print, .ant-btn {
            display: none !important;
          }
          .printable-report {
            padding: 0 !important;
            min-height: auto !important;
            background: transparent !important;
          }
          ${
            isCustomRender
              ? `
            .receipt-container {
              padding: 0 !important;
              margin: 0 !important;
              display: flex !important;
              justify-content: center !important;
              align-items: flex-start !important;
              width: 100% !important;
            }
            .receipt-container > div {
              box-shadow: none !important;
              border: none !important;
              width: 100% !important;
              max-width: 100% !important;
              padding: 0 !important;
              margin: 0 auto !important;
            }
          `
              : `
            .custom-report-table {
              border: none !important;
            }
            .ant-table-container {
              border: 1px solid #ddd !important;
            }
            .ant-table-thead > tr > th {
              background-color: #f5f5f5 !important;
              color: #000 !important;
              border-bottom: 2px solid #ddd !important;
            }
            .ant-table-cell {
              padding: 8px 12px !important;
              border-bottom: 1px solid #ddd !important;
              font-size: 11px !important;
            }
          `
          }
        }
      `}</style>
    </div>
  );
};
